import React, { useCallback, useEffect, useMemo, useState } from "react";

import { LoadingButton } from "@mui/lab";
import {
  Box,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";

import { Group } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/groups_pb";

import { DEFAULT_REGION_CODE } from "../../constants/index";
import { useCommonStyles } from "../../styled-utils";
import mainStyles from "../../styles";
import { GroupByRegion } from "../../types/permissions";
import DisplayError from "../shared/display-error";
import TransferList from "./components/transfer-list";
import settingStyles from "./styles";
import { listGroups, listPermissions } from "api/endpoints/periscope-admin";
import { callOptionsHeaders } from "api/helpers";
import { regionValuesArr } from "api/index";
import { actionTypeToString } from "constants/periscope-actions";
import downloadFile from "helpers/download-file";

const PermissionAppSettings = () => {
  const classes = mainStyles();
  const settingClasses = settingStyles();
  const styles = useCommonStyles();
  const [groupsList, setGroupsList] = useState<Group[]>([]);
  const [groupsByRegion, setGroupsByRegion] = useState<GroupByRegion>({
    us: {
      groupsList: [],
    },
    eu: {
      groupsList: [],
    },
    au: {
      groupsList: [],
    },
  });
  const [selectedGroup, setSelectedGroup] = useState<Group>();
  const [errorMessage, setErrorMessage] = useState("");
  const [downloadCsvError, setDownloadCsvError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isGroupsLoading, setIsGroupsLoading] = useState(false);
  const defaultRegion = useMemo(() => {
    return regionValuesArr.length === 1
      ? regionValuesArr[0]
      : DEFAULT_REGION_CODE;
  }, []);

  const handleSelectChange = (event: SelectChangeEvent<Group>) => {
    const groupToSelect = groupsList.find(
      (group) => group.name === event.target.value
    );

    if (groupToSelect) {
      setSelectedGroup(groupToSelect);
    }
  };

  const getGroupsListReq = async () => {
    setIsGroupsLoading(true);
    await Promise.all(
      regionValuesArr.map((regionCode) => {
        return listGroups(callOptionsHeaders(regionCode))
          .then((groupsListResp) => {
            if (regionCode === defaultRegion) {
              setGroupsList([...groupsListResp]);
            }
            const updatedGroupsByRegion = { ...groupsByRegion };
            updatedGroupsByRegion[regionCode].groupsList = [...groupsListResp];
            setGroupsByRegion(updatedGroupsByRegion);
          })
          .catch((error) => {
            setErrorMessage(error.message);
          });
      })
    );
    setIsGroupsLoading(false);
  };

  const downloadCSV = async () => {
    try {
      // Get permissions data
      setIsLoading(true);
      const permissions = await listPermissions(
        callOptionsHeaders(defaultRegion)
      );

      if (!permissions || permissions.length === 0) {
        setDownloadCsvError("No permissions data available to download");
        return;
      }

      // Convert data to CSV format
      let csvContent =
        ["groupName", "actionTypeName", "actionTypeValue"].join(",") + "\n";

      // Add data rows
      permissions.forEach((permission) => {
        const row = [
          permission.groupName,
          actionTypeToString(permission.actionType),
          permission.actionType,
        ].join(",");
        csvContent += row + "\n";
      });

      // Use the downloadFile helper to trigger the download
      const fileName = `permissions-${
        new Date().toISOString().split("T")[0]
      }.csv`;
      downloadFile(fileName, csvContent);
      setDownloadCsvError("");
    } catch (error: unknown) {
      console.error("Error downloading Permissions CSV:", error);
      setDownloadCsvError(
        `Failed to download Permissions CSV: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
    } finally {
      setIsLoading(false);
    }
  };

  // eslint-disable-next-line
  const getGroupsList = useCallback(getGroupsListReq, [defaultRegion]);
  useEffect(() => {
    getGroupsList();
  }, [getGroupsList]);

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} sx={styles.spaceTopLarge}>
        <DisplayError errorMessage={downloadCsvError} />
        <Box
          display="flex"
          alignItems="flex-start"
          gap={2}
          flexWrap="wrap"
          justifyContent="flex-end"
        >
          <FormControl variant="outlined" size="small" sx={classes.formControl}>
            {groupsList.length !== 0 && (
              <>
                <InputLabel id="select-group-label">Group</InputLabel>
                <Select
                  labelId="select-group-label"
                  label="Group"
                  id="select-group"
                  value={selectedGroup}
                  onChange={handleSelectChange}
                  sx={classes.selector}
                >
                  {groupsList.map((group) => {
                    return (
                      <MenuItem value={group.name} key={group.name}>
                        {group.name}
                      </MenuItem>
                    );
                  })}
                </Select>
              </>
            )}
            {isGroupsLoading && (
              <CircularProgress
                color="primary"
                variant="indeterminate"
                thickness={3}
                size={30}
                sx={settingClasses.smallLoadingSpinner}
              />
            )}
          </FormControl>
          <LoadingButton
            variant="contained"
            color="primary"
            size="small"
            onClick={downloadCSV}
            disabled={isLoading}
            loading={isLoading}
            loadingPosition="center"
            endIcon={<span />}
            sx={{ minHeight: "40px" }}
          >
            Export
          </LoadingButton>
        </Box>
      </Grid>

      <DisplayError errorMessage={errorMessage} />
      {selectedGroup && (
        <TransferList
          setErrorMessage={setErrorMessage}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          selectedGroup={selectedGroup}
          groupsByRegion={groupsByRegion}
        />
      )}
    </Grid>
  );
};

export default PermissionAppSettings;
